/** Tooltip copy for Analytics sections, charts, and KPIs */
export const ANALYTICS_HELP = {
  // Dashboard sections
  "section-overview":
    "Core usage KPIs and trend charts for the selected date range. Use Compare to see change vs the previous period of equal length.",
  "section-growth":
    "Acquisition, attribution, installs, and signup/login performance — how users arrive and register.",
  "section-usage":
    "In-app behaviour after install: screens, investment funnel, engagement, and weekly retention cohorts.",
  "section-marketing":
    "Sales and marketing signals: hot leads, product performance, campaigns, re-engagement, events, activity feed, and geography.",
  "section-errors":
    "Operational health: automated alerts, top client/API errors, and PostHog data-quality checks.",

  // Overview KPIs
  "kpi-dau":
    "Daily Active Users — distinct users with at least one tracked event on each day in the period.",
  "kpi-wau":
    "Weekly Active Users — distinct users active in each rolling 7-day window.",
  "kpi-mau":
    "Monthly Active Users — distinct users active in each rolling 30-day window.",
  "kpi-page_views":
    "Total page/screen view events (page_view and screen analytics) in the selected period.",
  "kpi-installs":
    "App installs from AppsFlyer install conversion or attribution-detected events.",
  "kpi-signups":
    "Completed registrations (signup_completed) — users who finished the signup flow.",
  "kpi-logins":
    "Successful sign-in events on the SignIn screen.",
  "kpi-investments":
    "Investment flows completed (investment_submitted) in the period.",
  "kpi-investment_amount":
    "Total EUR amount from submitted investments in the period.",

  // Overview panels
  "active-users":
    "DAU, WAU, and MAU over time. Toggle Daily vs Weekly to switch granularity. Sourced from PostHog active-user queries.",
  "acquisition-sources":
    "Where users came from — paid media, organic, referral, or unknown — based on attribution properties on events.",
  platform:
    "Split of users and events by mobile platform (iOS vs Android) in the selected filters.",
  "installs-trend":
    "Install volume over time from AppsFlyer or attribution events. Shows daily or weekly totals.",
  attribution:
    "AppsFlyer attribution summary: paid vs organic mix and channel breakdown for attributed installs.",
  auth:
    "Signup and login counts with period-over-period change. Opens funnels for step-level conversion.",
  "signup-funnel":
    "Mobile signup steps: flow started → password → OTP → completed. Extended view includes the email step and step-level PostHog events.",
  "step-completions":
    "Count of signup_step_completed events per step — confirms which UI steps users finished.",
  "attributed-installs":
    "Installs broken down by marketing channel or media source from AppsFlyer attribution.",
  "investment-funnel":
    "End-to-end investment journey from product view through submission, with step conversion rates.",
  "top-screens":
    "Most viewed screens/pages in the app for the period, ranked by view count.",
  "lead-to-investor":
    "Engagement funnel from first page view through product interest, CTA tap, flow start, to investment submitted.",
  retention:
    "Weekly cohort retention — percentage of each signup cohort returning in subsequent weeks.",
  "hot-leads":
    "CRM-matched users who tapped Invest but did not finish. Use Resync mappings to link PostHog IDs to CRM contacts.",
  products:
    "Funds/products ranked by views, CTA clicks, and investment starts in the period.",
  "campaign-roi":
    "Campaign or source performance when attribution data is available (installs, signups, investments).",
  "re-engagement":
    "Users who showed intent (e.g. started signup or investment) but did not complete — candidates for outreach.",
  events:
    "All tracked event types grouped by category with counts and mini trend sparklines.",
  "latest-activity":
    "Most recent events in the selected date range. Refreshes on load — not a live websocket feed.",
  realtime:
    "Most recent events in the selected date range. Refreshes on load — not a live websocket feed.",
  "geographic-insights":
    "User distribution by country and city from geo properties on PostHog events. Links to the full map view.",
  alerts:
    "Automated insights flagged from metric thresholds and anomalies in the dashboard data.",
  errors:
    "Most frequent client-side or API error events captured in PostHog.",
  "data-quality":
    "Checks for missing distinct IDs, anonymous users, and event mapping gaps affecting report accuracy.",
  "drop-offs":
    "Users who exited signup or investment flows before completing — broken down by last step reached.",
  engagement:
    "Engagement funnel metrics and step conversion for product views through investment submission.",

  // User 360
  "user360-kpis":
    "Per-user summary for the selected period: sessions, events, screens, investments, and error counts.",
  "user360-milestones":
    "Key journey checkpoints reached by this user (install, signup, login, investment) within the date range.",
  "user360-activity":
    "Daily event and session counts for this user — shows engagement intensity over time.",
  "user360-top-events":
    "Most frequent PostHog event names fired by this user in the period.",
  "user360-top-screens":
    "Screens or pages this user viewed most often (page_view breakdown).",
  "user360-device":
    "Device brand, OS, and app version observed from event properties.",
  "user360-location":
    "Geo locations inferred from PostHog geoIP or event geo properties.",
  "user360-investment":
    "CRM subscription totals vs PostHog investment_submitted events for this user.",
  "user360-products":
    "Product/fund views and invest CTA taps attributed to this user.",
  "user360-funnels":
    "Binary funnels showing whether this user reached each signup and investment step (1 = yes, 0 = no).",
  "user360-errors":
    "Error and API failure events recorded for this user.",
  "user360-health":
    "Engagement health score (0–100) based on activity depth, recency, and journey progress.",
  "user360-retention":
    "Last seen date, days since active, and active days in the selected window.",
  "user360-insights":
    "Auto-generated narrative highlights about this user’s behaviour in the period.",
  "user360-timeline":
    "Chronological PostHog events for this user. Filter by event name, platform, or app version.",

  // Devices page
  "devices-page":
    "Device brands (Apple, Samsung, etc.) parsed from mobile event properties for connected CRM users.",
  "devices-distribution":
    "Share of users by device manufacturer — donut chart.",
  "devices-by-brand":
    "User count per brand — bar chart. Click a brand card below to list individual users.",
  "devices-cards":
    "Click a brand to open the user list. Top model shown when available from event properties.",

  // Geo map page
  "geo-page":
    "World map of users with latitude/longitude from PostHog events. Clusters aggregate nearby users.",
  "geo-world-map":
    "Zoom and click cluster counts or pins to inspect users at that location.",
  "geo-top-countries":
    "Countries ranked by user count with coordinates in the period.",
  "geo-countries-table":
    "Click a row to load all users for that country (and city when filtered).",

  // Connected users list
  "connected-users":
    "CRM users linked to PostHog distinct IDs and AppsFlyer attribution. Open User 360 for individual activity.",
};

export const getAnalyticsHelp = (key) => ANALYTICS_HELP[key] || null;
